import { t, i18n } from './i18n.js';
import { notify } from './notify.js';
import { escapeHtml, cleanName } from './utils.js';
import { stripCountry } from './country.js';
import { urlSyncEnabled, publicUrl } from './deeplink.js';
import { account } from './account.js';
import { openExternal } from './external.js';

/* Compartir un canal, una película o una serie. En la app independiente (sin servidor propio) no hay un enlace que le sirva
   a otra persona, así que se comparte un mensaje de texto con lo esencial: título, año, nota, géneros, sinopsis y el enlace
   al tráiler. NUNCA lleva la dirección del vídeo ni nada del proveedor (llevan usuario y contraseña). */

const PLOT_MAX = 280;

/** Base de los enlaces compartibles: el origen en el navegador, el servidor con sesión iniciada, y nada en la app independiente
    (sin servidor propio no hay enlace que le sirva a otra persona: se comparte texto). */
export function linkBase() {
    if (urlSyncEnabled()) return location.origin;
    return account.token ? account.baseUrl() : '';
}

/** Dirección de la página de compartir (filoteca.com/s): los chats la leen para enseñar la carátula, el título y la sinopsis de
    ese título. Solo lleva título, año, idioma y, si hay servidor propio, la ruta interna de la ficha: nada del proveedor. */
export function previewLink(kind, { title = '', year = '', program = '', internal = '' } = {}) {
    const base = linkBase() || 'https://filoteca.com';
    const q = new URLSearchParams({ k: kind === 'vod' ? 'movie' : kind === 'live' ? 'channel' : 'series', t: String(title || '').slice(0, 80) });
    if (/^\d{4}$/.test(String(year || ''))) q.set('y', String(year));
    if (program) q.set('p', String(program).slice(0, 120));
    q.set('l', i18n.lang || 'es');
    if (internal) q.set('u', internal);
    return base + '/s?' + q.toString();
}

/** Enlace a una ficha o a un canal, o '' si no hay (app independiente). */
export function shareLink(kind, id, uidOf) {
    const base = linkBase();
    if (!base) return '';
    if (kind === 'live') return base + '/watch/live/' + uidOf('live', id);
    return base + publicUrl((kind === 'vod' ? '/movie/' : '/series/') + id + '/');
}

const clip = (s, n) => {
    const x = String(s || '').replace(/\s+/g, ' ').trim();
    return x.length > n ? x.slice(0, n - 1).replace(/\s+\S*$/, '') + '…' : x;
};

/** Texto para compartir una película o una serie. `trailerUrl` opcional (YouTube). */
export function titleShareText(info, kind, { trailerUrl = '', link = '' } = {}) {
    if (!info) return '';
    const name = stripCountry(info.name || '');
    const lines = [];
    lines.push(name + (info.year ? ' (' + info.year + ')' : ''));
    const meta = [];
    const rating = Number(info.rating);
    if (rating > 0) meta.push('★ ' + rating.toFixed(1));
    const mins = Math.round((Number(info.durationSecs) || 0) / 60);
    if (kind === 'vod' && mins > 0) meta.push(mins + ' min');
    if (kind === 'series' && Array.isArray(info.seasons) && info.seasons.length) meta.push(t('share.seasons', { n: info.seasons.length }));
    if (info.genre) meta.push(String(info.genre).split(/[,/|]/).map((g) => g.trim()).filter(Boolean).slice(0, 3).join(', '));
    if (meta.length) lines.push(meta.join(' · '));
    const plot = clip(info.plot, PLOT_MAX);
    if (plot) lines.push('', plot);
    if (trailerUrl) lines.push('', t('share.trailer') + ': ' + trailerUrl);
    lines.push('', t('share.footer'));
    if (link) lines.push(link);
    return lines.join('\n');
}

/** Texto para compartir un canal, con lo que echan ahora si se sabe. */
export function channelShareText(channel, programTitle = '', { link = '' } = {}) {
    if (!channel) return '';
    const name = cleanName(channel.name || '');
    const lines = [t('share.watching', { name })];
    if (programTitle) lines.push(clip(programTitle, 120));
    lines.push('', t('share.footer'));
    if (link) lines.push(link);
    return lines.join('\n');
}

/** Último enlace http(s) de un texto (para los destinos que lo piden aparte, como Telegram). */
function lastLink(text) {
    const m = String(text || '').match(/https?:\/\/\S+$/);
    return m ? m[0] : '';
}

/** Destinos de compartir para cuando el sistema no tiene hoja propia (Windows, navegadores de escritorio). */
function shareTargets(all, subject, url, text) {
    const link = url || lastLink(text) || 'https://filoteca.com';
    const body = url ? text : String(text || '').replace(link, '').trim();
    return [
        { icon: 'chat', label: 'WhatsApp', href: 'https://wa.me/?text=' + encodeURIComponent(all) },
        { icon: 'send', label: 'Telegram', href: 'https://t.me/share/url?url=' + encodeURIComponent(link) + '&text=' + encodeURIComponent(body || subject) },
        { icon: 'mail', label: t('share.email'), mailto: 'mailto:?subject=' + encodeURIComponent(subject) + '&body=' + encodeURIComponent(all) }
    ];
}

async function copyAll(all, url, text) {
    try {
        await navigator.clipboard.writeText(all);
        notify.toast(t(url && !text ? 'detail.linkCopied' : 'share.copied'), { icon: 'content_copy', duration: 2200 });
        return 'copied';
    } catch (e) {
        window.app.dialog.alert(`<textarea class="share-url" readonly rows="6" style="width:100%">${escapeHtml(all)}</textarea>`, t('detail.share'));
        return 'dialog';
    }
}

/** Ventana «Compartir por…» con WhatsApp, Telegram, correo y copiar. Devuelve cuando se elige algo o se cierra. */
function shareDialog({ title, text, url, all }) {
    return new Promise((resolve) => {
        const app = window.app;
        const targets = shareTargets(all, title, url, text);
        let done = false;
        const finish = (r) => { if (!done) { done = true; resolve(r); } };
        const choose = (fn) => () => { fn(); };
        const buttons = targets.map((tg) => ({
            text: tg.label,
            onClick: choose(async () => {
                if (tg.mailto) {
                    if (window.electronBridge && window.electronBridge.openExternal) { try { await window.electronBridge.openExternal(tg.mailto); } catch (e) { /* sin programa de correo */ } } else window.location.href = tg.mailto;
                } else await openExternal(tg.href);
                finish('shared');
            })
        }));
        buttons.push({ text: t('share.copy'), onClick: () => { copyAll(all, url, text).then(finish); } });
        buttons.push({ text: t('common.cancel'), onClick: () => finish('cancelled') });
        const dlg = app.dialog.create({ title: t('share.via'), text: '', buttons, verticalButtons: true });
        dlg.on('closed', () => finish('cancelled'));
        dlg.open();
    });
}

/** Compartir con lo que tenga el aparato: la hoja de Android (WhatsApp, Telegram, correo…), la del navegador, o una ventana con
    los destinos más usados y «Copiar» (Windows y navegadores de escritorio, donde no hay hoja de compartir que pueda abrir la app). */
export async function shareContent({ title = '', text = '', url = '' } = {}) {
    const all = [text, url].filter(Boolean).join('\n');
    const tv = typeof window !== 'undefined' && window.cordova && window.cordova.plugins && window.cordova.plugins.filotecaTv;
    if (tv && typeof tv.share === 'function') {
        try { if (await tv.share(title, all)) return 'shared'; } catch (e) { /* sin hoja: se sigue */ }
    }
    try {
        if (typeof navigator !== 'undefined' && navigator.share) {
            const data = { title };
            if (text) data.text = text;
            if (url) data.url = url;
            await navigator.share(data);
            return 'shared';
        }
        if (!window.electronBridge) notify.toast(t('share.noSheet'), { icon: 'info', duration: 3500 }); // en la app de Windows ya se sabe que no hay
    } catch (e) {
        if (e && e.name === 'AbortError') return 'cancelled'; // el usuario cerró la hoja
        // El navegador tiene hoja de compartir pero la ha rechazado: se dice por qué (y se sigue con la ventana de destinos).
        notify.toast(t('share.sheetFailed', { why: String((e && (e.name || e.message)) || e).slice(0, 80) }), { icon: 'info', duration: 5000 });
    }
    if (window.app && window.app.dialog && typeof window.app.dialog.create === 'function') return shareDialog({ title, text, url, all });
    return copyAll(all, url, text);
}
